/**
 * Slide Registry
 * Central configuration for all slide types with metadata and default content
 */

import type { SlideType } from '../supabase/client';
import { FileText, Video, FileImage, Globe, Orbit, Music, Tag, CheckCircle, AlertCircle, MessageSquare, BarChart2, Type, Link2, Trophy, Pencil, StickyNote, Cloud, ListOrdered, AlignLeft } from 'lucide-react';

/**
 * Slide type configuration containing metadata and defaults
 */
export interface SlideTypeConfig {
  type: SlideType;
  category: 'content' | 'activity';
  label: string;
  icon: any;
  defaultContent: Record<string, any>;
}

/**
 * Complete registry of all slide types with their configurations
 */
export const SLIDE_REGISTRY: SlideTypeConfig[] = [
  // ===== CONTENT SLIDES (7 types) =====
  {
    type: 'text_image',
    category: 'content',
    label: 'Text & Image',
    icon: FileText,
    defaultContent: {
      title: 'Slide Title',
      heading: 'Slide Title',
      content: 'Your content here',
      imageUrl: null,
      layout: 'single',
      content2: '',
      imageUrl2: null,
    },
  },
  {
    type: 'video',
    category: 'content',
    label: 'Video Slide',
    icon: Video,
    defaultContent: {
      videoUrl: '',
      provider: 'youtube',
      autoplay: false,
      description: '',
    },
  },
  {
    type: 'pdf',
    category: 'content',
    label: 'PDF Viewer',
    icon: FileImage,
    defaultContent: {
      pdfUrl: '',
      title: 'Document',
    },
  },
  {
    type: 'web_content',
    category: 'content',
    label: 'Web Content',
    icon: Globe,
    defaultContent: {
      url: '',
      description: 'Explore this website',
    },
  },
  {
    type: 'field_trip',
    category: 'content',
    label: 'Field Trip / 360 View',
    icon: Orbit,
    defaultContent: {
      panoramaMode: 'url',
      panoramaUrl: '',
      panoramaImageUrl: null,
      location: 'Virtual Location',
      description: 'Take a virtual field trip',
    },
  },
  {
    type: 'audio',
    category: 'content',
    label: 'Audio Slide',
    icon: Music,
    defaultContent: {
      audioUrl: '',
      audioTitle: 'Audio Clip',
      transcript: '',
      showControls: true,
    },
  },
  {
    type: 'text_only',
    category: 'content',
    label: 'Text Only',
    icon: AlignLeft,
    defaultContent: {
      content: '',
      backgroundColor: '#ffffff',
    },
  },
  // ===== ACTIVITY SLIDES (14 types) =====
  // IMPORTANT: If you add/remove activity types, you MUST update these files:
  // 1. supabase/functions/server/grade-calculator.ts (gradableSlides array)
  // 2. supabase/migrations/022_sync_all_analytics_data.sql (activity type IN clause)
  // 3. supabase/migrations/023_fix_activity_types_list.sql (latest migration)
  {
    type: 'multiple_choice',
    category: 'activity',
    label: 'Multiple Choice',
    icon: CheckCircle,
    defaultContent: {
      question: 'Your question here',
      options: ['Option A', 'Option B', 'Option C', 'Option D'],
      correctAnswer: 0,
      shuffleOptions: false,
      points: 10,
    },
  },
  {
    type: 'true_false',
    category: 'activity',
    label: 'True or False',
    icon: AlertCircle,
    defaultContent: {
      question: 'Your statement here',
      correctAnswer: true,
      points: 10,
    },
  },
  {
    type: 'open_ended',
    category: 'activity',
    label: 'Open-Ended',
    icon: MessageSquare,
    defaultContent: {
      question: 'Your question here',
      placeholder: 'Students type their answer here...',
      points: 10,
    },
  },
  {
    type: 'poll',
    category: 'activity',
    label: 'Poll',
    icon: BarChart2,
    defaultContent: {
      question: 'Your poll question',
      options: ['Option 1', 'Option 2', 'Option 3'],
      points: 10,
    },
  },
  {
    type: 'fill_blanks',
    category: 'activity',
    label: 'Fill in the Blanks',
    icon: Type,
    defaultContent: {
      instruction: 'Fill in the blanks',
      sentence: 'The capital of France is [blank1] and it is known for the [blank2].',
      answers: ['Paris', 'Eiffel Tower'],
      points: 10,
    },
  },
  {
    type: 'matching_pairs',
    category: 'activity',
    label: 'Matching Pairs',
    icon: Link2,
    defaultContent: {
      instruction: 'Match the items',
      pairs: [
        { left: 'Item 1', right: 'Match 1' },
        { left: 'Item 2', right: 'Match 2' },
      ],
      points: 10,
    },
  },
  {
    type: 'time_to_climb',
    category: 'activity',
    label: 'Time to Climb',
    icon: Trophy,
    defaultContent: {
      title: 'Quiz Game',
      quizType: 'multiple_choice',
      questions: [
        {
          question: 'Sample question',
          options: ['A', 'B', 'C', 'D'],
          correctAnswer: 0,
        },
      ],
      shuffleQuestions: false,
      timeLimit: 30,
      timeLimitMode: 'per_question',
      points: 50,
    },
  },
  {
    type: 'draw_it',
    category: 'activity',
    label: 'Draw-It',
    icon: Pencil,
    defaultContent: {
      instructions: 'Draw your answer',
      backgroundColor: '#ffffff',
      backgroundImageUrl: null,
      points: 10,
    },
  },
  {
    type: 'collaborate_board',
    category: 'activity',
    label: 'Collaborate Board',
    icon: StickyNote,
    defaultContent: {
      prompt: 'Collaborate with your classmates',
      allowDrawing: true,
      allowText: true,
      maxNotes: 3,
      points: 10,
    },
  },
  {
    type: 'word_cloud',
    category: 'activity',
    label: 'Word Cloud',
    icon: Cloud,
    defaultContent: {
      question: 'Enter your response',
      maxWords: 3,
      points: 10,
    },
  },
  {
    type: 'label_diagram',
    category: 'activity',
    label: 'Label Diagram',
    icon: Tag,
    defaultContent: {
      imageUrl: null,
      labels: [],
      instructions: 'Label the diagram',
      points: 10,
    },
  },
  {
    type: 'quiz',
    category: 'activity',
    label: 'Quiz',
    icon: CheckCircle,
    defaultContent: {
      questions: [],
      shuffleQuestions: false,
      timeLimit: 60,
      points: 100,
    },
  },
  {
    type: 'drag_drop',
    category: 'activity',
    label: 'Drag & Drop',
    icon: Link2,
    defaultContent: {
      instruction: 'Drag and drop items to their correct positions',
      items: [],
      targets: [],
      points: 10,
    },
  },
  {
    type: 'sequencing',
    category: 'activity',
    label: 'Sequencing',
    icon: ListOrdered,
    defaultContent: {
      instruction: 'Drag the items into the correct order',
      sequenceMode: 'custom',
      items: [],
      correctOrder: [],
      points: 10,
    },
  },
];

/**
 * Get configuration for a specific slide type
 */
export function getSlideConfig(type: SlideType): SlideTypeConfig | undefined {
  return SLIDE_REGISTRY.find((config) => config.type === type);
}

/**
 * Get default content for a slide type
 */
export function getDefaultContentForSlideType(type: SlideType): Record<string, any> {
  const config = getSlideConfig(type);
  return config?.defaultContent || {};
}

/**
 * Get all content slide types
 */
export function getContentSlideTypes(): SlideTypeConfig[] {
  return SLIDE_REGISTRY.filter((config) => config.category === 'content');
}

/**
 * Get all activity slide types
 */
export function getActivitySlideTypes(): SlideTypeConfig[] {
  return SLIDE_REGISTRY.filter((config) => config.category === 'activity');
}

/**
 * Get all slide types (for LessonBuilder)
 */
export function getAllSlideTypes(): SlideTypeConfig[] {
  return SLIDE_REGISTRY;
}

/**
 * Check if a type is a content slide
 */
export function isContentSlideType(type: SlideType): boolean {
  return getContentSlideTypes().some((config) => config.type === type);
}

/**
 * Check if a type is an activity slide
 */
export function isActivitySlideType(type: SlideType): boolean {
  return getActivitySlideTypes().some((config) => config.type === type);
}

/**
 * Check if an activity slide renders its own title/header
 * Some complex activities (like CollaborateBoard, WordCloud, etc.) render their own titles
 */
export function activityRendersOwnTitle(type: SlideType): boolean {
  const activitiesWithOwnTitles: SlideType[] = [
    'collaborate_board',
    'word_cloud',
    'matching_pairs',
    'label_diagram',
    'time_to_climb',
  ];
  return activitiesWithOwnTitles.includes(type);
}
